/**
 * 词意配图 OSS 地址解析
 * 命名规则与 cibeiai/scripts/lib/wordImageShared.mjs、utils/wordImage.js 一致
 */

/** OSS 配图根路径（filename 拼在 /images/ 后） */
export const WORD_IMAGE_OSS_BASE = 'https://yingyuwangzhan.oss-cn-beijing.aliyuncs.com/images'

/** 配图展示比例（与 batch resize 600×400、3:2 一致） */
export const WORD_IMAGE_ASPECT = '3 / 2'
export const WORD_IMAGE_ASPECT_RATIO = 3 / 2

// eslint-disable-next-line no-control-regex
const INVALID_FS_CHARS = /[<>:"/\\|?*\x00-\x1f]/g
const CJK_CHARS = /[\u4e00-\u9fff]/
const PURE_NUMERIC = /^[\d,]+$/

const BRACKET_CHARS = /[()[\]{}<>（）【】「」『』《》〈〉]/g

/**
 * 文件名与 en 对齐：去各类括号、拒中文与纯数字、其余 Windows 非法字符 → _
 * （与 cibeiai/scripts/lib/wordImageShared.mjs 一致）
 */
export function enToFilename (en) {
  let trimmed = String(en || '').trim()
  if (!trimmed) return ''
  if (/^\{[^{}]+\}$/.test(trimmed)) {
    trimmed = trimmed.slice(1, -1).trim()
  }
  trimmed = trimmed.replace(BRACKET_CHARS, '').replace(/\s+/g, ' ').trim()
  if (!trimmed || CJK_CHARS.test(trimmed)) return ''
  const base = trimmed.replace(INVALID_FS_CHARS, '_').replace(/\s+/g, ' ').slice(0, 180).trim()
  if (!base) return ''
  const digitsOnly = base.replace(/,/g, '')
  if (PURE_NUMERIC.test(base) && /^\d+$/.test(digitsOnly)) return ''
  return base
}

/** 配图磁盘/OSS 文件名，如 apple → apple.png；(Autumn) → Autumn.png */
export function entryFilename (en) {
  const base = enToFilename(en)
  return base ? `${base}.png` : ''
}

/** 首字母大写、其余小写（And、Apple） */
export function titleCaseRestLower (text) {
  const s = String(text || '')
  if (!s) return s
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

/** 每个单词首字母大写（At The Age Of） */
export function titleCaseEachWord (text) {
  return String(text || '')
    .split(' ')
    .filter(Boolean)
    .map((w) => titleCaseRestLower(w))
    .join(' ')
}

/** 仅句首大写，其余保持 en 原样（与部分长句配图一致） */
export function sentenceCase (text) {
  const s = String(text || '')
  if (!s) return s
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * 大小写/写法候选（OSS 区分大小写；上传时常见 apple.png / Apple.png 并存）
 * @param {string} en
 * @param {{ filename?: string }} [options] manifest 中的 filename 优先
 * @returns {string[]} 去重后的 .png 文件名，按尝试优先级排列
 */
export function entryFilenameCandidates (en, options = {}) {
  const base = enToFilename(en)
  const manifestName = String(options.filename || '').trim().replace(/\.png$/i, '')

  if (!base && !manifestName) return []

  const baseNames = []
  const add = (name) => {
    const n = String(name || '').trim()
    if (n) baseNames.push(n)
  }

  if (manifestName) add(manifestName)
  if (base) {
    const isLowerAlpha = base === base.toLowerCase() && /^[a-z]+$/.test(base)
    // OSS 上 3 字母及以上小写词常见 Apple.png；2 字母词常见 an.png / at.png
    if (isLowerAlpha && base.length >= 3) {
      add(titleCaseRestLower(base))
      add(base)
    } else {
      add(base)
      add(titleCaseRestLower(base))
    }
    add(base.toLowerCase())
    add(base.toUpperCase())
    add(titleCaseEachWord(base))
    add(sentenceCase(base))
    if (/^[a-zA-Z]{2,4}$/.test(base)) {
      add(base.toUpperCase())
    }
  }

  const seen = new Set()
  const result = []
  for (const name of baseNames) {
    const filename = name.toLowerCase().endsWith('.png') ? name : `${name}.png`
    if (seen.has(filename)) continue
    seen.add(filename)
    result.push(filename)
  }
  return result
}

function buildOssImageUrl (filename) {
  if (!filename) return ''
  // OSS 对象名与本地文件名一致；URL 中空格等字符需 encode（at.png 不变，at the age of.png → at%20the%20age%20of.png）
  return `${WORD_IMAGE_OSS_BASE}/${encodeURIComponent(filename)}`
}

/**
 * @param {string} en 英文单词或短语
 * @param {{ filename?: string }} [options]
 * @returns {string} OSS 完整 URL，无法解析时返回空字符串
 */
export function resolveWordOssImageUrl (en, options) {
  const candidates = resolveWordOssImageUrlCandidates(en, options)
  return candidates[0] || ''
}

/**
 * @param {string} en
 * @param {{ filename?: string }} [options]
 * @returns {string[]} 按优先级排列的 OSS URL（加载失败时可依次尝试）
 */
export function resolveWordOssImageUrlCandidates (en, options = {}) {
  const hasKey = isWordImageKey(en) || String(options.filename || '').trim()
  if (!hasKey) return []
  return entryFilenameCandidates(en, options).map(buildOssImageUrl)
}

/** 将接口相对路径 cover 转为可访问 URL */
export function resolveApiCoverUrl (cover) {
  const url = String(cover || '').trim()
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  if (url.startsWith('//')) return `https:${url}`
  const base = String(process.env.VUE_APP_BASE_API || '').replace(/\/$/, '')
  if (url.startsWith('/') && base) return `${base}${url}`
  return url
}

/**
 * 查词配图候选：优先 OSS（按英文词匹配），其次接口返回的 cover
 * @param {string} wordKey 英文单词；中文查词时传 speakWord / return_phrase
 * @param {string} [apiCover] 接口 cover_url / cover / pic_url
 * @returns {string[]}
 */
export function resolveWordImageUrlCandidates (wordKey, apiCover = '') {
  const ossCandidates = resolveWordOssImageUrlCandidates(wordKey)
  const picUrl = resolveApiCoverUrl(apiCover)
  if (!picUrl) return ossCandidates
  if (ossCandidates.includes(picUrl)) return ossCandidates
  return [...ossCandidates, picUrl]
}

/**
 * @param {string} en
 * @param {{ filename?: string }} [options]
 * @returns {{ url: string, filename: string } | null}
 */
export function resolveWordOssImage (en, options) {
  const filenames = entryFilenameCandidates(en, options)
  const filename = filenames[0] || ''
  if (!filename || !isWordImageKey(en)) return null
  return {
    url: buildOssImageUrl(filename),
    filename
  }
}

/** 可映射到配图文件名的 key（中文 / 纯数字 / 去括号后为空 → false） */
export function isWordImageKey (text) {
  return Boolean(enToFilename(text))
}

/** /course/course/word 保底：仅单词级 key，整句/问句不走查词接口 */
export function isWordApiLookupKey (text) {
  const value = String(text || '').trim()
  if (!value || !isWordImageKey(value)) return false
  if (/[?!]/.test(value)) return false
  return value.split(/\s+/).filter(Boolean).length <= 1
}

/** 调试：对比原始词、文件名与最终 URL */
export function debugWordOssImage (en, tag = 'wordImage', options) {
  const trimmed = String(en || '').trim()
  const filenames = entryFilenameCandidates(trimmed, options)
  const filename = filenames[0] || ''
  const isKey = isWordImageKey(trimmed)
  const url = isKey ? buildOssImageUrl(filename) : ''
  const info = {
    tag,
    en: trimmed,
    enJson: JSON.stringify(trimmed),
    filename,
    filenameCandidates: filenames,
    isWordImageKey: isKey,
    url
  }
  console.log(`[WordImageResolve] ${tag}`, info)
  return info
}
